import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { SITE } from "../mock";
import { Loader2, CheckCircle2, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnquiryForm({ compact = false, title = "Get in Touch – We're Happy to Help", subtitle = "Share a few quick details and our expert will call you back within minutes.", source = "website" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", code: "+91", honeypot: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  const validate = () => {
    if (form.honeypot) return "Spam detected";
    if (!form.name.trim() || form.name.trim().length < 2) return "Please enter your full name";
    if (!/^\d{7,15}$/.test(form.phone.trim())) return "Please enter a valid phone number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError(""); setStatus("sending");
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        code: form.code,
        email: form.email.trim() || null,
        message: form.message.trim() || null,
        source,
        honeypot: form.honeypot,
      };
      await axios.post(`${API}/leads`, payload);
      setStatus("success");
      setForm({ name: "", phone: "", email: "", message: "", code: "+91", honeypot: "" });
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || "Something went wrong. Please call us directly.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="bg-white rounded-lg p-6 text-center border border-[#ecdfc9]">
        <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="font-serif-display text-2xl font-bold text-[#8B1E1E] mb-2">Thank you!</h3>
        <p className="text-gray-600 text-sm mb-4">Your enquiry has been received. Our expert will reach out shortly on the details you have provided.</p>
        <a href={`tel:${SITE.phoneRaw}`} className="btn-primary justify-center w-full">
          <Phone className="w-4 h-4" /> Call Now {SITE.phone}
        </a>
        <button onClick={() => setStatus("idle")} className="mt-3 text-sm text-gray-500 hover:text-[#8B1E1E] underline">Submit another enquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`bg-white rounded-lg p-5 md:p-6 border border-[#ecdfc9] shadow-[0_8px_30px_rgba(139,30,30,0.08)] ${compact ? "" : ""}`}>
      <div className="text-center mb-4">
        <h3 className="font-serif-display text-xl md:text-2xl font-bold text-[#8B1E1E]">{title}</h3>
        <span className="gold-divider" />
        <p className="text-xs md:text-sm text-gray-500">{subtitle}</p>
      </div>

      <input type="text" name="hp" tabIndex={-1} autoComplete="off" value={form.honeypot}
        onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
        style={{ position: "absolute", left: "-9999px", opacity: 0 }} aria-hidden="true" />

      <div className="space-y-3">
        <input type="text" placeholder="Full Name*" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm" />

        <div className="flex gap-2">
          <select value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
            className="px-2 py-2.5 border border-gray-300 rounded bg-white text-sm focus:border-[#8B1E1E] outline-none">
            <option value="+91">🇮🇳 +91</option>
            <option value="+971">🇦🇪 +971</option>
            <option value="+1">🇺🇸 +1</option>
            <option value="+44">🇬🇧 +44</option>
            <option value="+65">🇸🇬 +65</option>
            <option value="+61">🇦🇺 +61</option>
          </select>
          <input type="tel" placeholder="Phone Number*" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, "") })}
            className="flex-1 px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm" />
        </div>

        <input type="email" placeholder="Email (optional)" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm" />

        {!compact && (
          <textarea placeholder="Your requirement (optional)" rows="3" value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm resize-none" />
        )}

        {error && <p className="text-red-600 text-xs">{error}</p>}

        <button type="submit" disabled={status === "sending"} className="btn-primary w-full justify-center pulse-ring">
          {status === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>GET CALL BACK</>}
        </button>

        <p className="text-[11px] text-gray-500 text-center leading-snug">
          By clicking submit, you agree to our{" "}
          <Link
            to="/privacy-policy"
            className="text-[#8B1E1E] underline hover:text-[#6f1616] font-medium"
          >
            Privacy Policy
          </Link>{" "}
          regarding how we collect, store, and use your lead data.
        </p>
      </div>
    </form>
  );
}
